import type React from 'react'
import { colors, fontSize, radius, shadow, spacing, transition } from '../../styles/theme'
import { fmtDelta, fmtNum, shortDay } from '../../utils/analyticsFormat'

export const RANGE_OPTIONS = [7, 30, 90, 365]

export const VIEWS_EMPTY_TEXT =
  'Views are recorded when documents are opened via share links, preview links, e-signature requests or embeds.'

export const card: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.lg,
  boxShadow: shadow.xs,
  minWidth: 0,
}

export const gridAuto = (min: number): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
  gap: spacing.md,
})

export const th: React.CSSProperties = {
  padding: `${spacing.sm} ${spacing.md}`,
  textAlign: 'left',
  fontSize: fontSize.xs,
  fontWeight: 600,
  color: colors.textSecondary,
  borderBottom: `1px solid ${colors.border}`,
  background: colors.background,
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

export const td: React.CSSProperties = {
  padding: `${spacing.sm} ${spacing.md}`,
  fontSize: fontSize.sm,
  color: colors.text,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap',
}

export const numTd: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

export function RangePicker({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div role="group" aria-label="Date range" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {RANGE_OPTIONS.map(d => (
        <button
          key={d}
          type="button"
          aria-pressed={days === d}
          onClick={() => onChange(d)}
          style={{
            padding: '5px 12px',
            border: `1px solid ${days === d ? colors.primary : colors.border}`,
            borderRadius: radius.full,
            background: days === d ? colors.primary : 'transparent',
            color: days === d ? colors.onPrimary : colors.textSecondary,
            fontSize: fontSize.sm,
            fontWeight: days === d ? 600 : 400,
            cursor: 'pointer',
            transition: transition.fast,
          }}
        >
          {d}d
        </button>
      ))}
    </div>
  )
}

export function TabStrip<T extends string>({ tabs, active, onChange }: {
  tabs: { id: T; label: string }[]; active: T; onChange: (id: T) => void
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex', gap: spacing.xs, borderBottom: `1px solid ${colors.border}`, marginBottom: spacing.lg,
        overflowX: 'auto', overflowY: 'hidden', minWidth: 0, maxWidth: '100%',
      }}
    >
      {tabs.map(t => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: `${spacing.sm} ${spacing.md}`,
            fontSize: fontSize.sm,
            fontWeight: active === t.id ? 600 : 400,
            color: active === t.id ? colors.text : colors.textSecondary,
            background: 'none',
            border: 'none',
            borderBottom: `2px solid ${active === t.id ? colors.primary : 'transparent'}`,
            marginBottom: -1,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

export function KpiCard({ label, value, change, hint, tone }: {
  label: string; value: string; change?: number | null; hint?: string; tone?: string
}) {
  return (
    <div style={card} data-testid={`kpi-${label}`}>
      <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: tone ?? colors.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {change != null && (
        <div style={{ fontSize: fontSize.xs, marginTop: 2, color: change > 0 ? colors.success : change < 0 ? colors.error : colors.textSecondary }}>
          {fmtDelta(change)} vs previous period
        </div>
      )}
      {hint && <div style={{ fontSize: fontSize.xs, marginTop: 2, color: colors.textMuted }}>{hint}</div>}
    </div>
  )
}

export function SectionTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, flexWrap: 'wrap' }}>
      <div style={{ fontWeight: 600, color: colors.text, fontSize: fontSize.base, flex: 1 }}>{children}</div>
      {right}
    </div>
  )
}

export function EmptyState({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div data-testid="analytics-empty" style={{ textAlign: 'center', padding: `${spacing.lg} ${spacing.md}`, color: colors.textSecondary }}>
      <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>{title}</div>
      {children && <div style={{ fontSize: fontSize.sm, maxWidth: 460, margin: '0 auto' }}>{children}</div>}
    </div>
  )
}

export interface Series { key: string; label: string; color: string }

/**
 * Grouped daily bars on one shared scale. Longer ranges are bucketed by week so a 365-day chart
 * stays legible on a phone.
 */
export function DailyChart({ rows, series, height = 140 }: {
  rows: ReadonlyArray<{ day: string }>; series: Series[]; height?: number
}) {
  const bucket = rows.length > 92 ? 7 : 1
  const cols: { day: string; values: number[] }[] = []
  for (let i = 0; i < rows.length; i += bucket) {
    const slice = rows.slice(i, i + bucket)
    cols.push({ day: slice[0].day, values: series.map(s => slice.reduce((a, r) => a + (Number((r as Record<string, unknown>)[s.key]) || 0), 0)) })
  }
  const max = Math.max(1, ...cols.flatMap(c => c.values))
  const labelEvery = Math.max(1, Math.ceil(cols.length / 7))
  const gap = cols.length > 40 ? '1px' : '3px'

  return (
    <div data-testid="daily-chart">
      <div style={{ fontSize: fontSize['2xs'], color: colors.textMuted, marginBottom: 4 }}>
        {bucket > 1 ? 'Per week' : 'Per day'} (max {fmtNum(max)})
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap, height, borderBottom: `1px solid ${colors.border}` }}>
        {cols.map(c => (
          <div
            key={c.day}
            title={`${shortDay(c.day)}${bucket > 1 ? ' (week)' : ''}: ${series.map((s, i) => `${fmtNum(c.values[i])} ${s.label.toLowerCase()}`).join(', ')}`}
            style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', gap: 1, height }}
          >
            {series.map((s, i) => (
              <div
                key={s.key}
                style={{
                  flex: 1,
                  height: c.values[i] > 0 ? Math.max(2, Math.round((c.values[i] / max) * height)) : 1,
                  background: c.values[i] > 0 ? s.color : colors.border,
                  opacity: c.values[i] > 0 ? 0.85 : 1,
                  borderRadius: '2px 2px 0 0',
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap, marginTop: 4 }}>
        {cols.map((c, i) => (
          <div key={c.day} style={{ flex: 1, minWidth: 0, fontSize: '9px', color: colors.textMuted, textAlign: 'center', overflow: 'visible', whiteSpace: 'nowrap', opacity: i % labelEvery === 0 ? 1 : 0 }}>
            {shortDay(c.day)}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.sm, flexWrap: 'wrap' }}>
        {series.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 10, height: 10, background: s.color, borderRadius: 2 }} />
            <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Horizontal bar breakdown; each bar is the item's share of the list's largest value. */
export function BarList({ items, emptyText = 'No data yet.', color = colors.primary }: {
  items: { label: string; value: number; sub?: string }[]; emptyText?: string; color?: string
}) {
  const shown = items.filter(i => i.value > 0)
  if (shown.length === 0) return <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>{emptyText}</div>
  const max = Math.max(...shown.map(i => i.value))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
      {shown.map(i => (
        <div key={i.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.sm, fontSize: fontSize.sm, marginBottom: 3 }}>
            <span style={{ color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.label}</span>
            <span style={{ color: colors.textSecondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {fmtNum(i.value)}{i.sub ? ` · ${i.sub}` : ''}
            </span>
          </div>
          <div style={{ height: 6, background: colors.surfaceMist, borderRadius: radius.full, overflow: 'hidden' }}>
            <div style={{ width: `${(i.value / max) * 100}%`, height: '100%', background: color, borderRadius: radius.full }} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function TableWrap({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>
    </div>
  )
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div role="alert" style={{
      background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`,
      borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md,
    }}>{message}</div>
  )
}
